import React from 'react'
import { Box, ButtonBase, ToggleButton, ToggleButtonGroup, Typography } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'
import {
  Apps as AppsIcon,
  Biotech as MicroscopeIcon,
  GroupWork as GroupWorkIcon,
  Science as ScienceIcon,
} from '@material-ui/icons'
import DnaIcon from 'src/views/AnalysisView/sampleTools/DnaIcon'

// Small building blocks shared by the New analysis panel. Type sizes follow the
// owner's rules: 12px floor, 13 to 14.5px for data, Geist Mono for sample IDs.

export const MONO_SX = { fontFamily: 'fontFamilyMono', fontSize: 13.5, letterSpacing: 0 }

export const GroupLabel = ({ children, sx }) => (
  <Typography component="div" sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary', mb: 1, ...sx }}>
    {children}
  </Typography>
)

export const Muted = ({ children, sx, component = 'div' }) => (
  <Typography component={component} sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45, ...sx }}>
    {children}
  </Typography>
)

export const ErrorLine = ({ children, sx }) => (
  <Typography component="div" sx={{ fontSize: 13, color: 'error.main', mt: 0.75, lineHeight: 1.4, ...sx }}>
    {children}
  </Typography>
)

// A text button that reads as a link.
export const TextLink = ({ children, onClick, disabled, sx, tone = 'primary', ...other }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    disableRipple
    sx={{
      font: 'inherit',
      fontSize: 13,
      fontWeight: 500,
      color: disabled ? 'text.disabled' : tone === 'muted' ? 'text.secondary' : `${tone}.main`,
      borderRadius: 0.5,
      px: 0.25,
      mx: -0.25,
      verticalAlign: 'baseline',
      '&:hover': { textDecoration: 'underline' },
      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
      ...sx,
    }}
    {...other}
  >
    {children}
  </ButtonBase>
)

/** A segmented control. options: [{ value, label, disabled? }] */
export const Segmented = ({ value, options, onChange, fullWidth = true, ariaLabel, size = 'normal' }) => {
  const theme = useTheme()
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_e, next) => {
        if (next !== null && next !== undefined) onChange(next)
      }}
      aria-label={ariaLabel}
      sx={{
        width: fullWidth ? '100%' : 'auto',
        p: '3px',
        gap: '3px',
        borderRadius: `${theme.shape.borderRadius}px`,
        backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.05 : 0.07),
        '& .MuiToggleButtonGroup-grouped': {
          flex: fullWidth ? 1 : 'none',
          border: 0,
          borderRadius: `${Math.max(theme.shape.borderRadius - 2, 4)}px !important`,
          height: size === 'small' ? 28 : 32,
          px: 1.25,
          fontSize: 13.5,
          fontWeight: 500,
          textTransform: 'none',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
          '&.Mui-selected': {
            color: 'text.primary',
            backgroundColor: 'background.paper',
            boxShadow: `0 1px 2px ${alpha('#000', theme.palette.mode === 'light' ? 0.12 : 0.4)}`,
            '&:hover': { backgroundColor: 'background.paper' },
          },
        },
      }}
    >
      {options.map((o) => (
        <ToggleButton key={o.value} value={o.value} disabled={o.disabled} disableRipple>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

// The icon of an analysis kind. Germline is the DNA in primary, somatic the
// microscope in violet; the group analyses are quiet (text.secondary) at rest, and
// a caller passes sx to colour them when selected.
export const KindIcon = ({ kind, sx }) => {
  const base = { fontSize: 20, ...sx }
  if (kind === 'somatic') return <MicroscopeIcon sx={{ color: 'somatic.main', ...base }} />
  if (kind === 'multisample') return <GroupWorkIcon sx={{ color: 'text.secondary', ...base }} />
  if (kind === 'cohort') return <AppsIcon sx={{ color: 'text.secondary', ...base }} />
  if (kind === 'carrier') return <ScienceIcon sx={{ color: 'text.secondary', ...base }} />
  return <DnaIcon sx={{ color: 'primary.main', ...base }} />
}

// Soft fill for quiet cells: inputs and cell buttons read as a list, not a grid of boxes.
export const cellFill = (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.04 : 0.06)

// Marks the line of cells whose hover shows the input borders (see cellInputSx).
export const CELL_ROW_ATTR = 'data-cell-row'
const ROW_HOVER = `[${CELL_ROW_ATTR}]:hover &:not(.Mui-focused):not(.Mui-focusVisible):not(.Mui-error):not(.Mui-disabled)`

// Compact input styles for table cells and short fields. At rest a soft fill with no
// border; the divider border shows while the row is hovered, primary on focus.
export const cellInputSx = (theme) => ({
  fontSize: 13.5,
  width: '100%',
  borderRadius: `${theme.shape.borderRadius}px`,
  border: '1px solid transparent',
  backgroundColor: cellFill(theme),
  px: 0.75,
  height: 30,
  transition: theme.transitions.create(['border-color', 'background-color'], { duration: 120 }),
  [ROW_HOVER]: { borderColor: theme.palette.divider },
  '&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled)': {
    borderColor: alpha(theme.palette.text.primary, 0.32),
  },
  '&.Mui-focused': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.background.paper },
  '&.Mui-error': { borderColor: theme.palette.error.main },
  '&.Mui-disabled': { backgroundColor: 'transparent' },
  '& input': { p: 0 },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', m: 0 },
})

// A cell that opens the row's details (Phenotype, Tumour type): the same soft fill
// as the inputs, a hover state and a focus ring.
export const cellButtonSx = (theme) => ({
  width: '100%',
  minWidth: 0,
  height: 30,
  px: 0.75,
  justifyContent: 'flex-start',
  fontSize: 13.5,
  borderRadius: `${theme.shape.borderRadius}px`,
  border: '1px solid transparent',
  backgroundColor: cellFill(theme),
  transition: theme.transitions.create(['border-color', 'background-color'], { duration: 120 }),
  [ROW_HOVER]: { borderColor: theme.palette.divider },
  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.07 : 0.1) },
  '&.Mui-focusVisible': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
})

// Scroll areas of the panel: the thumb shows on hover only and the gutter is kept,
// so content does not shift when a scrollbar appears.
export const quietScrollSx = (theme) => ({
  scrollbarGutter: 'stable',
  scrollbarColor: 'transparent transparent',
  '&:hover': { scrollbarColor: `${alpha(theme.palette.text.primary, 0.24)} transparent` },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent' },
  '&:hover::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.24) },
})

export const Stack = ({ gap = 3, children, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap, ...sx }}>{children}</Box>
)
